import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import A11yDialogInstance from 'a11y-dialog';
import classNames from 'classnames';

import { Cart } from '@components/cart/Cart';
import { Svg } from '@components/svg/Svg';

import { getIsActiveUrl } from '@common/utils/navigation';
import { useCart } from '@context/CartContext';

import styles from './navigation.module.scss';
import { NavigationAddOn } from './NavigationAddOn';
import { NavigationAnchor } from './NavigationAnchor';
import { NavigationFullscreenOpenButtonContent } from './NavigationFullscreenOpenButtonContent';

const getNavigationLinks = () => {
	return [
		{
			url: '/events',
			title: 'Calendar',
		},
		{
			url: 'https://web.elsewhere.club/pages/memberships',
			title: 'Memberships',
		},
		{
			url: 'https://web.elsewhere.club/pages/private-events',
			title: 'Private Events',
		},
		{
			url: 'https://web.elsewhere.club/pages/sponsorships',
			title: 'Sponsorships',
		},
	];
};

type NavigationProps = {
	navigationFullscreenInstance?: A11yDialogInstance;
};

let scrollTimeout: NodeJS.Timeout;

export const Navigation = ({
	navigationFullscreenInstance,
}: NavigationProps) => {
	const router = useRouter();
	const { cart } = useCart();

	const [showMobileHeader, setShowMobileHeader] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);

	const navigationContainer = useRef<HTMLDivElement>(null);
	const navigationMobileHeader = useRef<HTMLDivElement>(null);

	const navigationLinks = getNavigationLinks();
	const visibleOnMobile = ['Calendar', 'Private Events'];
	const visibleOnSmallDesktopIfNotLoggedIn = ['Calendar', 'Private Events'];
	const visibleOnMediumDesktop = ['Calendar', 'Private Events'];

	const isHomepage = router.pathname === '/';
	const isCartVisible = cart && cart.totalQuantity > 0;

	const headerScrollToggleTransitionTimeInMs = 120;
	const timeAfterWhichHeaderScrollToggleIfNoScrollActivityInMs = 500; // necessary in case browser that doesn't support scrollend (e.g. Safari)
	let mobileHeaderHeight = navigationMobileHeader.current?.clientHeight || 0;

	const toggleMobileHeader = () => {
		navigationContainer.current?.style.setProperty(
			'--transition-top',
			`${headerScrollToggleTransitionTimeInMs}ms ease-in-out top`,
		);

		if (window.scrollY > lastScrollY && window.scrollY > mobileHeaderHeight) {
			setShowMobileHeader(false);
		} else {
			setShowMobileHeader(true);
		}

		if (window.scrollY > mobileHeaderHeight) setLastScrollY(window.scrollY);

		setTimeout(() => {
			navigationContainer.current?.style.setProperty(
				'--transition-top',
				'0ms ease-in-out top',
			);
		}, headerScrollToggleTransitionTimeInMs);
	};

	const scrollMobileHeader = () => {
		clearTimeout(scrollTimeout);
		const relativeScrollY = window.scrollY - Math.max(0, lastScrollY);

		navigationContainer.current?.style.setProperty(
			'--scroll-offset',
			`${relativeScrollY}px`,
		);

		if (mobileHeaderHeight) {
			if (
				relativeScrollY > mobileHeaderHeight &&
				window.scrollY > mobileHeaderHeight
			) {
				if (showMobileHeader) {
					setShowMobileHeader(false);
				}
				setLastScrollY(window.scrollY);
			} else if (relativeScrollY < -mobileHeaderHeight) {
				if (!showMobileHeader) {
					setShowMobileHeader(true);
				}
				setLastScrollY(window.scrollY);
			}
		}

		scrollTimeout = setTimeout(() => {
			toggleMobileHeader();
		}, timeAfterWhichHeaderScrollToggleIfNoScrollActivityInMs);
	};

	useEffect(() => {
		if (navigationMobileHeader.current) {
			mobileHeaderHeight = navigationMobileHeader.current.clientHeight;
		}
	}, [navigationMobileHeader.current]);

	useEffect(() => {
		window.addEventListener('scroll', scrollMobileHeader);
		window.addEventListener('scrollend', toggleMobileHeader);

		return () => {
			window.removeEventListener('scroll', scrollMobileHeader);
			window.removeEventListener('scrollend', toggleMobileHeader);
		};
	}, [lastScrollY]);

	return (
		<nav
			aria-label="Main navigation"
			className={classNames(styles['navigation-container'], {
				[styles['navigation-container--home']]: isHomepage,
				[styles['navigation-container--hide-mobile-header']]: !showMobileHeader,
			})}
			ref={navigationContainer}
		>
			<div
				className={styles['navigation__mobile-header']}
				ref={navigationMobileHeader}
			>
				<Link
					aria-label="Homepage"
					className={classNames(
						styles.navigation__logo,
						styles['navigation__logo--mobile'],
					)}
					href="/"
				>
					<Svg id="logo" />
				</Link>
				<ul
					className={classNames(
						styles['navigation__anchor-list--mobile-header'],
						styles['navigation__anchor-list'],
						'font-t5-bold',
						'list-unstyled',
					)}
				>
					<li className={classNames(styles['navigation__account-button'])}>
						<NavigationAddOn showCart={false} />
					</li>
				</ul>
			</div>
			<div className={styles.navigation}>
				<Link
					aria-label="Homepage"
					className={classNames(
						styles.navigation__logo,
						styles['navigation__logo--desktop'],
					)}
					href="/"
				>
					<Svg id="logo" />
				</Link>
				<ul
					className={classNames(
						styles['navigation__anchor-list'],
						styles['navigation__anchor-list--desktop'],
						'font-t5-bold',
						'list-unstyled',
					)}
				>
					{navigationLinks.map(({ url, title }) => {
						const isActive = getIsActiveUrl(router, url);

						return (
							<li
								className={classNames(styles.navigation__anchor, {
									[styles[
										'navigation__anchor--hide-before-xs-if-cart-visible'
									]]: isCartVisible && title !== 'Calendar',
									[styles['navigation__anchor--visible-on-mobile']]:
										visibleOnMobile.includes(title),
									[styles['navigation__anchor--visible-on-md']]:
										visibleOnSmallDesktopIfNotLoggedIn.includes(title),
									[styles['navigation__anchor--visible-on-lg']]:
										visibleOnMediumDesktop.includes(title),
								})}
								key={url}
							>
								<NavigationAnchor isActive={isActive} url={url}>
									{title}
								</NavigationAnchor>
							</li>
						);
					})}
					<li className={classNames(styles['navigation__account-button'])}>
						<NavigationAddOn
							hideLabelOnDesktop
							overrideDesktopColor={isHomepage ? 'sand' : 'black'}
						/>
					</li>
				</ul>
				{isCartVisible ? (
					<div
						className={classNames(styles.navigation__cart, {
							[styles['navigation__cart--homepage']]: isHomepage,
						})}
					>
						<Cart />
					</div>
				) : null}
				<button
					aria-label="Open fullscreen navigation"
					className={styles['navigation__open-fullscreen-button']}
					onClick={() => navigationFullscreenInstance?.show()}
					type="button"
				>
					<NavigationFullscreenOpenButtonContent
						hideBubble
						overrideMobileOpenButtonColor="sand"
					/>
				</button>
			</div>
		</nav>
	);
};
